import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ShoppingBag, Search, Filter, RefreshCw, Clock, CheckCircle,
  XCircle, ChefHat, Truck, Eye, MoreHorizontal, Phone, MapPin,
  Calendar, User, Receipt, AlertCircle, RotateCcw, DollarSign
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PaymentParts, SplitBadge } from '@/components/payments/SplitPayments';
import { LIGHT_TONES, orderStatus, paymentStatus } from '@/lib/orderStatus';
import { formatDateTime } from '@/lib/restaurantTime';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';
import {
  errorText, fetchRefundSummary, outcomeText, refundOrderPayment, toCents, voidOrderPayment,
} from '@/lib/orderRefunds';

// Refund reasons
const REFUND_REASONS = [
  { value: "customer_request", label: "Customer Request" },
  { value: "order_error", label: "Order Error" },
  { value: "quality_issue", label: "Quality Issue" },
  { value: "never_received", label: "Never Received" },
  { value: "duplicate", label: "Duplicate Order" },
  { value: "other", label: "Other" }
];

// Order status configuration
const ORDER_STATUSES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  preparing: { label: 'Preparing', color: 'bg-orange-100 text-orange-800', icon: ChefHat },
  ready: { label: 'Ready', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-purple-100 text-purple-800', icon: Truck },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
};
// any other state (awaiting_payment, served, ...) is shown as itself - never as "Pending"
const statusOf = (value) => ORDER_STATUSES[value]
  || { label: orderStatus(value).label, color: LIGHT_TONES[orderStatus(value).tone], icon: Clock };

// Status progression
const STATUS_FLOW = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'completed'];

function OrderCard({ order, onStatusChange, onView, onQuickRefund }) {
  const status = statusOf(order.status);
  const StatusIcon = status.icon;
  const currentIndex = STATUS_FLOW.indexOf(order.status);
  const nextStatus = currentIndex >= 0 ? STATUS_FLOW[currentIndex + 1] : null; // an unpaid order is not advanced
  
  // Check if order is eligible for refund (completed or paid orders)
  const canRefund = ['paid', 'partially_refunded'].includes(order.payment_status) && !order.is_voided;
  
  return (
    <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`order-${order.id}`}>
      <CardContent className="p-3 md:p-4">
        <div className="flex items-start justify-between mb-2 md:mb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-base md:text-lg">#{order.id}</span>
              <Badge className={`${status.color} text-xs`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">{status.label}</span>
                <span className="sm:hidden">{status.label.split(' ')[0]}</span>
              </Badge>
            </div>
            <p className="text-xs md:text-sm text-gray-500">
              {formatDateTime(order.created_at, order.timezone, { year: undefined })}
            </p>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(order)}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Receipt className="h-4 w-4 mr-2" />
                Print Receipt
              </DropdownMenuItem>
              {canRefund && (
                <DropdownMenuItem 
                  className="text-orange-600"
                  onClick={() => onQuickRefund(order)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Quick Refund
                </DropdownMenuItem>
              )}
              {order.status !== 'cancelled' && order.status !== 'completed' && (
                <DropdownMenuItem 
                  className="text-red-600"
                  onClick={() => onStatusChange(order.id, 'cancelled')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Order
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Customer Info */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b">
          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
            {order.customer_name?.charAt(0) || 'C'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{order.customer_name}</p>
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Phone className="h-3 w-3" />
              {order.customer_phone || 'No phone'}
            </p>
          </div>
          <Badge variant="outline" className="capitalize">
            {order.order_type}
          </Badge>
        </div>
        
        {/* Items */}
        <div className="space-y-1 mb-3">
          {order.items?.slice(0, 3).map((item, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-slate-600">{item.quantity}x {item.name}</span>
              <span className="font-medium">${item.total_price?.toFixed(2)}</span>
            </div>
          ))}
          {order.items?.length > 3 && (
            <p className="text-sm text-slate-400">+{order.items.length - 3} more items</p>
          )}
        </div>
        
        {/* Special Instructions */}
        {order.special_instructions && (
          <div className="mb-3 p-2 bg-yellow-50 rounded-lg border border-yellow-100">
            <p className="text-sm text-yellow-800">
              <AlertCircle className="h-3 w-3 inline mr-1" />
              {order.special_instructions}
            </p>
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t">
          <div>
            <p className="text-xl font-bold">${order.total?.toFixed(2)}</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge variant="outline" className={LIGHT_TONES[paymentStatus(order.payment_status).tone]}>
                {paymentStatus(order.payment_status).label}
              </Badge>
              <SplitBadge split={order.split} />
            </div>
          </div>
          
          {nextStatus && order.status !== 'cancelled' && (
            <Button 
              size="sm"
              className="bg-gradient-to-r from-blue-600 to-purple-600"
              onClick={() => onStatusChange(order.id, nextStatus)}
            >
              {nextStatus === 'confirmed' && 'Accept'}
              {nextStatus === 'preparing' && 'Start Preparing'}
              {nextStatus === 'ready' && 'Mark Ready'}
              {nextStatus === 'out_for_delivery' && 'Out for Delivery'}
              {nextStatus === 'completed' && 'Complete'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function OrdersPage() {
  const context = useOutletContext() || {};
  const { token = localStorage.getItem('backoffice_token'), manager = null } = context;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('status') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Quick Refund state
  const [showQuickRefund, setShowQuickRefund] = useState(false);
  const [refundOrder, setRefundOrder] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  // What the server says about this order's payment (card via Aster Gateway, or cash).
  const [refundSummary, setRefundSummary] = useState(null);
  const [refundSummaryError, setRefundSummaryError] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    confirmed: 0,
    preparing: 0,
    ready: 0,
    completed: 0
  });

  const getHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/merchant/orders`, getHeaders());
      const ordersList = response.data || [];
      setOrders(ordersList);
      
      // Calculate stats
      const newStats = {
        pending: ordersList.filter(o => o.status === 'pending').length,
        confirmed: ordersList.filter(o => o.status === 'confirmed').length,
        preparing: ordersList.filter(o => o.status === 'preparing').length,
        ready: ordersList.filter(o => o.status === 'ready').length,
        completed: ordersList.filter(o => o.status === 'completed').length
      };
      setStats(newStats);
      
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    if (token) {
      fetchOrders();
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchOrders, 30000);
      return () => clearInterval(interval);
    }
  }, [token, fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/merchant/orders/${orderId}/status?status=${newStatus}`,
        {},
        getHeaders()
      );
      toast.success(`Order #${orderId} updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOrders();
    setRefreshing(false);
    toast.success('Orders refreshed');
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  // Quick Refund handlers: the server's numbers (captured, already refunded,
  // still refundable) decide; a card refund / void is sent to Aster Gateway
  // and only shows as done once it approved.
  const handleQuickRefund = async (order) => {
    setRefundOrder(order);
    setRefundAmount('');
    setRefundReason('');
    setRefundSummary(null);
    setRefundSummaryError(null);
    setShowQuickRefund(true);
    try {
      const summary = await fetchRefundSummary(order.id, getHeaders());
      setRefundSummary(summary);
      setRefundAmount(((summary.refundable_cents || 0) / 100).toFixed(2));
    } catch (error) {
      setRefundSummaryError(errorText(error, 'Could not load this order\'s payment'));
    }
  };

  const finishReversal = (view) => {
    if (view && view.is_final !== false && view.status === 'approved') {
      toast.success(outcomeText(view, refundOrder.id));
      setShowQuickRefund(false);
      setRefundOrder(null);
    } else {
      toast.error(outcomeText(view, refundOrder.id));
    }
    fetchOrders();
  };

  const processQuickRefund = async () => {
    if (!refundOrder || !refundReason || !refundSummary) {
      toast.error('Please select a reason for the refund');
      return;
    }
    const amountCents = toCents(refundAmount);
    if (amountCents === null) {
      toast.error('Please enter a valid refund amount');
      return;
    }
    if (amountCents > refundSummary.refundable_cents) {
      toast.error(`At most $${(refundSummary.refundable_cents / 100).toFixed(2)} can be refunded`);
      return;
    }
    setProcessingRefund(true);
    try {
      const view = await refundOrderPayment(refundOrder.id, {
        amountCents,
        reason: refundReason,
        notes: 'Quick refund from Orders page',
        refundedCentsBefore: refundSummary.refunded_cents_before,
      }, getHeaders());
      finishReversal(view);
    } catch (error) {
      toast.error(errorText(error, 'The refund did not go through'));
      fetchOrders();
    } finally {
      setProcessingRefund(false);
    }
  };

  const processVoid = async () => {
    if (!refundOrder || !refundReason) {
      toast.error('Please select a reason for the void');
      return;
    }
    setProcessingRefund(true);
    try {
      const view = await voidOrderPayment(refundOrder.id, {
        reason: refundReason, notes: 'Void from Orders page',
      }, getHeaders());
      finishReversal(view);
    } catch (error) {
      toast.error(errorText(error, 'The void did not go through'));
      fetchOrders();
    } finally {
      setProcessingRefund(false);
    }
  };

  // Filter orders based on tab and search
  const filteredOrders = orders.filter(order => {
    // Tab filter
    if (activeTab !== 'all' && order.status !== activeTab) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        order.id?.toString().includes(query) ||
        order.customer_name?.toLowerCase().includes(query) ||
        order.customer_phone?.includes(query)
      );
    }
    return true;
  });

  return (
    <div className="space-y-4 md:space-y-6" data-testid="orders-page">
      {/* Header - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-500 text-sm md:text-base">Manage and track all customer orders</p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="w-full sm:w-auto"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats - Responsive grid */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-4">
        {Object.entries(stats).map(([status, count]) => {
          const config = ORDER_STATUSES[status];
          return (
            <Card 
              key={status} 
              className={`cursor-pointer bg-white border-0 shadow-sm hover:shadow-md transition-all ${activeTab === status ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setActiveTab(status)}
            >
              <CardContent className="p-2 md:p-4 text-center">
                <p className="text-xl md:text-3xl font-bold text-gray-900">{count}</p>
                <p className="text-xs md:text-sm text-gray-500 capitalize truncate">{status}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters - Mobile responsive with horizontal scroll */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:flex-1">
          <TabsList className="bg-gray-100 w-full overflow-x-auto flex-nowrap justify-start">
            <TabsTrigger value="all" className="text-xs md:text-sm whitespace-nowrap">All</TabsTrigger>
            <TabsTrigger value="pending" className="relative text-xs md:text-sm whitespace-nowrap">
              Pending
              {stats.pending > 0 && (
                <span className="ml-1 px-1 py-0.5 text-[10px] bg-yellow-500 text-white rounded-full">
                  {stats.pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="preparing" className="text-xs md:text-sm whitespace-nowrap">Preparing</TabsTrigger>
            <TabsTrigger value="ready" className="text-xs md:text-sm whitespace-nowrap">Ready</TabsTrigger>
            <TabsTrigger value="completed" className="text-xs md:text-sm whitespace-nowrap">Done</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Orders Grid - Responsive */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
        </div>
      ) : filteredOrders.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {filteredOrders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              onStatusChange={handleStatusChange}
              onView={handleViewDetails}
              onQuickRefund={handleQuickRefund}
            />
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-20 text-center">
            <ShoppingBag className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No orders found</h3>
            <p className="text-slate-500">
              {activeTab !== 'all' ? `No ${activeTab} orders at the moment` : 'Orders will appear here when customers place them'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Order Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Order #{selectedOrder?.id}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <Badge className={statusOf(selectedOrder.status).color}>
                  {statusOf(selectedOrder.status).label}
                </Badge>
                <span className="text-sm text-slate-500">
                  {formatDateTime(selectedOrder.created_at, selectedOrder.timezone)}
                </span>
              </div>
              
              {/* Customer */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="font-medium">{selectedOrder.customer_name}</p>
                    <p className="text-sm text-slate-500">{selectedOrder.customer_phone}</p>
                  </div>
                </div>
                {selectedOrder.delivery_address && (
                  <div className="flex items-start gap-3 mt-2 pt-2 border-t">
                    <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                    <p className="text-sm">{selectedOrder.delivery_address}</p>
                  </div>
                )}
              </div>
              
              {/* Items */}
              <div>
                <h4 className="font-semibold mb-2">Order Items</h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium">{item.quantity}x {item.name}</p>
                      </div>
                      <p className="font-semibold">${item.total_price?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Special Instructions */}
              {selectedOrder.special_instructions && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800">Special Instructions:</p>
                  <p className="text-sm text-yellow-700">{selectedOrder.special_instructions}</p>
                </div>
              )}
              
              {/* Total */}
              <div className="flex justify-between text-lg font-bold pt-3 border-t">
                <span>Total</span>
                <span>${selectedOrder.total?.toFixed(2)}</span>
              </div>
              {selectedOrder.split && (
                <div className="space-y-2" data-testid="order-split-payments">
                  <h4 className="font-semibold flex items-center gap-2">Payments <SplitBadge split={selectedOrder.split} /></h4>
                  <PaymentParts payments={selectedOrder.payments} timezone={selectedOrder.timezone} />
                </div>
              )}
              
              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowDetails(false)}>
                  Close
                </Button>
                {['paid', 'partially_refunded'].includes(selectedOrder.payment_status) && !selectedOrder.is_voided && (
                  <Button 
                    variant="outline"
                    className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                    onClick={() => {
                      setShowDetails(false);
                      handleQuickRefund(selectedOrder);
                    }}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Refund
                  </Button>
                )}
                {STATUS_FLOW.indexOf(selectedOrder.status) >= 0 && selectedOrder.status !== 'completed' && (
                  <Button 
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600"
                    onClick={() => {
                      const nextStatus = STATUS_FLOW[STATUS_FLOW.indexOf(selectedOrder.status) + 1];
                      if (nextStatus) {
                        handleStatusChange(selectedOrder.id, nextStatus);
                        setShowDetails(false);
                      }
                    }}
                  >
                    Update Status
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Refund Dialog */}
      <Dialog open={showQuickRefund} onOpenChange={setShowQuickRefund}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-orange-600" />
              Quick Refund
            </DialogTitle>
            <DialogDescription>
              Process a refund for Order #{refundOrder?.id}
            </DialogDescription>
          </DialogHeader>
          
          {refundOrder && (
            <div className="space-y-4">
              {refundSummaryError && (
                <p className="text-sm text-red-600" data-testid="refund-summary-error">{refundSummaryError}</p>
              )}
              {refundSummary && !refundSummary.can_refund && (
                <p className="text-sm text-amber-700" data-testid="refund-unavailable">
                  {refundSummary.unavailable_reason || 'Nothing is left to refund on this order'}
                </p>
              )}
              {refundSummary && refundSummary.pending_cents > 0 && (
                <p className="text-sm text-gray-600">
                  ${(refundSummary.pending_cents / 100).toFixed(2)} is waiting for Aster Gateway's answer
                </p>
              )}
              {/* Order Summary */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-500">Customer</span>
                  <span className="font-medium">{refundOrder.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Order Total</span>
                  <span className="font-bold">${refundOrder.total?.toFixed(2)}</span>
                </div>
                {refundSummary && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Paid by</span>
                      <span className="font-medium">
                        {refundSummary.tender === 'card' ? 'Card (Aster Gateway)'
                          : refundSummary.tender === 'online_card' ? 'Online card'
                          : (refundSummary.tender || 'Unknown').replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Already refunded</span>
                      <span className="font-medium">${((refundSummary.refunded_cents || 0) / 100).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
              
              {/* Refund Amount */}
              <div>
                <Label>Refund Amount *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max={refundSummary ? (refundSummary.refundable_cents / 100).toFixed(2) : undefined}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="pl-9"
                    placeholder="0.00"
                  />
                </div>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto text-xs text-blue-600"
                  disabled={!refundSummary}
                  onClick={() => setRefundAmount(((refundSummary?.refundable_cents || 0) / 100).toFixed(2))}
                >
                  Refund all that is left (${((refundSummary?.refundable_cents || 0) / 100).toFixed(2)})
                </Button>
              </div>
              
              {/* Refund Reason */}
              <div>
                <Label>Reason *</Label>
                <Select value={refundReason} onValueChange={setRefundReason}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {REFUND_REASONS.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowQuickRefund(false)}>
              Cancel
            </Button>
            {refundSummary?.can_void && (
              <Button
                variant="outline"
                onClick={processVoid}
                disabled={processingRefund || !refundReason}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="void-payment"
              >
                Void card payment
              </Button>
            )}
            <Button 
              onClick={processQuickRefund}
              disabled={processingRefund || !refundReason || !refundAmount || !refundSummary?.can_refund}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {processingRefund ? 'Processing...' : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
